import { Stack, Box, Typography } from "@mui/material";
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DropzoneArea } from "react-mui-dropzone";
import { SnackbarContext } from "../../contexts/Snackbar";
import { isHeicFile, maxFileUploadSize, supportedFileTypes } from "../../enums/Consts";
import { snackbarSeverity } from "../../hooks/useSnackbar";
import SimpleAlert from "../display/SimpleAlert";
import FileChips from "./FileChips";

export const fileControlField = "files";

export default function FileUploadBody({
  numberOfFileLimit = 20,
  message = "MaximumFileSize5MB",
  severity = "info",
  allowedFileTypes = supportedFileTypes
}) {
  const methods = useFormContext();
  const selectedFiles = methods.watch(fileControlField) || [];
  const control = methods.control;
  const { handleSnackbar } = React.useContext(SnackbarContext);

  const { append } = useFieldArray({ name: fileControlField, control });
  const { t } = useTranslation();

  const limitReached = selectedFiles.length >= numberOfFileLimit;

  function handleSelectedFile(newFiles) {
    const fileNames = selectedFiles.map((file) => file.name);

    const filteredNewFiles = newFiles.filter(
      (file) => !fileNames.includes(file.name),
    );

    const remainingSlots = numberOfFileLimit - selectedFiles.length;
    if (remainingSlots <= 0) return;

    const filesToAppend = filteredNewFiles.slice(0, remainingSlots);

    filesToAppend.forEach((file) => {
      append(file);
    });
  }

  return (
    <Stack spacing={2}>
      <SimpleAlert severity={severity} message={message} />
      {limitReached ? (
        <Box
          className="custom-dropzone-root"
          sx={{
            border: "2px dashed",
            borderColor: "warning.main",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: 150,
            padding: 3,
            backgroundColor: "action.disabledBackground",
          }}
        >
          <Typography className="dropZoneText" color="warning.main">
            {t("MaximumFileLimitReached", { count: numberOfFileLimit })}
          </Typography>
        </Box>
      ) : (
        <DropzoneArea
          onDropRejected={(rejectedFiles) => {
            const file = rejectedFiles[0];
            if (isHeicFile(file)) {
              handleSnackbar("HeicNotSupportedSelectOther", snackbarSeverity.ERROR);
              return;
            }
            if (file && file.size > maxFileUploadSize) {
              handleSnackbar("FileTooLarge", snackbarSeverity.ERROR);
            } else if (file && !allowedFileTypes.includes(file.type)) {
              handleSnackbar("UnsupportedFileType", snackbarSeverity.ERROR);
            } else {
              handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
            }
          }}
          acceptedFiles={allowedFileTypes}
          dropzoneParagraphClass="dropZoneText"
          dropzoneClass="custom-dropzone-root"
          fileObjects={selectedFiles}
          onChange={(newFiles) => handleSelectedFile(newFiles)}
          dropzoneText={t("DragAndDropAFile")}
          filesLimit={numberOfFileLimit}
          maxFileSize={maxFileUploadSize}
          showAlerts={false}
          clearOnUnmount={true}
          showPreviewsInDropzone={false}
        />
      )}
      <FileChips />
    </Stack>
  );
}